import { type HandoffReasonCode, stateWord, UNKNOWN_STATE_WORD } from "@lafayette/domain";
import type {
  StaffAttentionItem,
  StaffAttentionSeverity,
  StaffAttentionSource,
  StaffAttentionSourceKey,
} from "@lafayette/schemas";

import { deepLinks } from "../navigation/deep-links";
import type { AreaKey } from "../navigation/offering";

// The words and the links of the attention sources (CHT-767). The API answers with closed keys, codes and counts only; every word that a
// person reads is here. A key with no entry is a compile error (`Record`), so a lane that adds a source key cannot forget its label.

type SourceCopy = Readonly<{
  /** The line of the list: what the count counts. */
  label: (count: number) => string;
  /** One plain sentence: why it needs action and what to do. */
  hint: string;
  /** The place that lists them all. */
  link: string;
  /** The area that place belongs to: the link is shown only while the area opens for the staff member (the offering). */
  area: AreaKey;
}>;

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

export const sourceCopy: Readonly<Record<StaffAttentionSourceKey, SourceCopy>> = {
  handoffs_at_risk: {
    label: (n) => plural(n, "handoff at risk", "handoffs at risk"),
    hint: "A customer waits for a person and the wait has a gap, a customer update, or is long.",
    link: deepLinks.inbox("AT_RISK"),
    area: "concierge",
  },
  coverage_exceptions: {
    label: (n) => plural(n, "coverage gap", "coverage gaps"),
    hint: "A waiting handoff has nobody who can take it. Assign a person.",
    // CHT-768: the Coverage area lists every gap and fixes it (the inbox filter lists only the conversations).
    link: deepLinks.coverage(),
    area: "coverage",
  },
  requests_unassigned: {
    label: (n) => plural(n, "request with no owner", "requests with no owner"),
    hint: "Nobody holds these requests. Claim one, or give it to a person.",
    link: deepLinks.requestQueue("UNASSIGNED"),
    area: "requests",
  },
  handoffs_waiting: {
    label: (n) => plural(n, "handoff waiting for you", "handoffs waiting for you"),
    hint: "A customer asked for a person. Accept the handoff.",
    link: deepLinks.inbox("WAITING_HANDOFFS"),
    area: "concierge",
  },
  requests_customer_answered: {
    label: (n) => plural(n, "request the customer answered", "requests the customers answered"),
    hint: "The customer replied to a question. The next move is yours.",
    link: deepLinks.requestQueue("IN_PROGRESS"),
    area: "requests",
  },
  requests_aging: {
    label: (n) => plural(n, "request that is aging", "requests that are aging"),
    hint: "These requests passed the age limit that the operator set.",
    link: deepLinks.requestQueue("AGING"),
    area: "requests",
  },
  conversations_customer_replied: {
    label: (n) => plural(n, "customer reply to answer", "customer replies to answer"),
    hint: "A customer wrote last in a conversation a person holds. Reply.",
    link: deepLinks.inbox("NEEDS_STAFF_ACTION"),
    area: "concierge",
  },
  conversations_need_staff: {
    label: (n) => plural(n, "conversation needs a person", "conversations need a person"),
    hint: "A customer wrote last, or the customer has open work.",
    link: deepLinks.inbox("NEEDS_STAFF_ACTION"),
    area: "concierge",
  },
  requests_assigned_to_me: {
    label: (n) => plural(n, "open request is yours", "open requests are yours"),
    hint: "Requests you own or that are assigned to you.",
    link: deepLinks.requestQueue("ASSIGNED_TO_ME"),
    area: "requests",
  },
  payments_to_reconcile: {
    label: (n) => plural(n, "payment to reconcile", "payments to reconcile"),
    hint: "A payment has an open exception, a provider call in doubt, or is open past its expiry. Reconcile it.",
    link: deepLinks.paymentsToReconcile(),
    area: "payments",
  },
};

/**
 * The short word of a "Needs you now" row: why this customer is on the list. One word group per source, so the row says what kind of
 * work waits, and the colour of the pill (by severity) is never the only cue. A `Record`: a new source key is a compile error here.
 */
export const needsWord: Readonly<Record<StaffAttentionSourceKey, string>> = {
  handoffs_at_risk: "At risk",
  coverage_exceptions: "No cover",
  requests_unassigned: "No owner",
  handoffs_waiting: "Wants a person",
  requests_customer_answered: "Customer answered",
  requests_aging: "Aging",
  conversations_customer_replied: "Customer replied",
  conversations_need_staff: "Needs a look",
  requests_assigned_to_me: "Yours",
  payments_to_reconcile: "To reconcile",
};

export const severityLabel: Readonly<Record<StaffAttentionSeverity, string>> = {
  urgent: "Urgent",
  attention: "Needs action",
  info: "For your information",
};

/** "50+" for a capped count, so a long queue never looks short. */
export function countText(source: Pick<StaffAttentionSource, "count" | "capped">): string {
  return source.capped ? `${source.count}+` : String(source.count);
}

/** The deep link of one item: a conversation opens in the workspace, a request opens its page. */
export function itemLink(item: StaffAttentionItem): string {
  return item.kind === "conversation"
    ? deepLinks.conversation(item.id)
    : deepLinks.request(item.id);
}

/** The area an item link opens in (a conversation is in Concierge, a request in Requests). */
export function itemArea(item: StaffAttentionItem): AreaKey {
  return item.kind === "conversation" ? "concierge" : "requests";
}

/**
 * The words of the handoff reason that a conversation item carries. They are written out here exactly as the Home page has always shown
 * them: one reason has a sentence, the other eight read as the code in lower case (a `Record` over the domain type, so a new reason is a
 * compile error here and cannot slip into the neutral word). The staff Concierge screens word the same reasons longer
 * (`conversations/labels.ts`); that is a separate vocabulary and is not changed here.
 */
const handoffReasonText: Readonly<Record<HandoffReasonCode, string>> = {
  CUSTOMER_REQUESTED_PERSON: "Customer asked for a person",
  SUPPLIER_CONTACT_REQUIRED: "supplier contact required",
  CONCIERGE_JUDGMENT_REQUIRED: "concierge judgment required",
  CATALOG_DATA_CONFLICT: "catalog data conflict",
  UNSUPPORTED_HIGH_IMPACT_REQUEST: "unsupported high impact request",
  COMPLAINT_SERVICE_RECOVERY: "complaint service recovery",
  PAYMENT_FULFILMENT_EXCEPTION: "payment fulfilment exception",
  REPEATED_NO_PROGRESS: "repeated no progress",
  POLICY_REVIEW_REQUIRED: "policy review required",
};

/**
 * What the code of an item says, in plain words. A request item carries the state of the request (the word comes from `@lafayette/domain`);
 * a conversation item carries the reason of its handoff. A code that is not known reads as the neutral word, never as the code. `null`: the
 * item has no code.
 */
export const itemCodeText = (
  kind: StaffAttentionItem["kind"],
  code: string | null,
): string | null => {
  if (code === null) return null;
  if (kind === "request") return stateWord("request", "staffShort", code);
  return Object.hasOwn(handoffReasonText, code)
    ? handoffReasonText[code as HandoffReasonCode]
    : UNKNOWN_STATE_WORD.staffShort;
};

/** One short line for the areas panel: what a role does in the area. */
export const areaDescriptions: Readonly<Record<string, string>> = {
  concierge: "Conversations with customers and the handoffs that wait for a person.",
  customers: "A customer's memory: what the concierge knows and may correct.",
  // WALK-2: the Privacy card had no line. The words are the first sentence of the Privacy page.
  privacy: "Export and deletion requests of customers.",
  requests: "The queue of concierge requests, from claim to completion.",
  trips: "Trips and itineraries of customers.",
  actions: "What customers are asked to do.",
  payments: "Payment requests and reconciliation.",
  catalog: "Catalog items, places, search and publishing.",
  imports: "Spreadsheet imports of catalog items.",
  operations: "Supervisor queues and operational controls.",
  staff: "Staff accounts, roles, access review and offboarding.",
  coverage: "Coverage gaps, who covers each customer, and who is away.",
  analytics: "The numbers of one customer: records, spend and service.",
};
